import crypto from "crypto";

// prettier-ignore
import { getByRowTypeServer, updateDB, runGetCommand, RIASTABLE, sendEmail } from "~/_server";
// prettier-ignore
import { matchABExpiry, RIASRecordType, schemeData, SubscriptionState } from "~/lib";
import { uploadFile } from "~/_server/s3";
import { SEND_TO_DEV } from "~/lib/constants";

export const BSD_EXPORT_KEY = "rias-regs.xml";

export type BsdExportResult = {
	result: "updated" | "skipped";
	date: number; // ms
	hash: string;
};

// Helper to format timestamps to YYYY-MM-DD
const formatDate = (timestamp: number | null): string => {
	if (!timestamp) return "";
	// Check if timestamp is in seconds or milliseconds
	const date = new Date(timestamp > 10000000000 ? timestamp : timestamp * 1000);
	return date.toISOString().split("T")[0];
};

// Reads the record of the last export (date + content hash).
export const getLastBsdExport = async (): Promise<{ date: number; hash: string } | null> => {
	"use server";

	const r = await runGetCommand({
		TableName: RIASTABLE,
		Key: { pk: "BSD", sk: "BSD" },
	});

	return r?.Item ? { date: r.Item.date as number, hash: r.Item.hash as string } : null;
};

// Builds the BSD register XML. If it differs from the last export it is
// uploaded to S3, recorded, and emailed to the Scottish Government register.
// Shared by the scheduled cron route and the admin dashboard button.
export const runBsdExport = async (): Promise<BsdExportResult> => {
	"use server";

	const { create, fragment } = await import("xmlbuilder2");

	// Align AB expiry with subscription expiry
	await matchABExpiry();

	// prettier-ignore
	const root = create( { version: "1.0" } )
		.ele( "SchemeProvider" )
		.ele( "SchemeProviderId" ).txt( "RIA" ).up()
		.ele( "SchemeProviderDesc" ).txt( "RIAS" ).up();

	// prettier-ignore
	const schemeSlugs = ["section-6-domestic", "section-6-non-domestic", "section-6-non-domestic-advanced"];
	const statusSlugs = [
		SubscriptionState.ACTIVE,
		SubscriptionState.RENEWING,
		SubscriptionState.EXPIRED,
		SubscriptionState.SUSPENDED,
		SubscriptionState.TERMINATED,
	];

	const usersRaw = await getByRowTypeServer(
		RIASRecordType.USER,
		5000,
		"#userId, #firstName, #lastName",
		{
			"#userId": "userId",
			"#firstName": "firstName",
			"#lastName": "lastName",
		},
	);

	const users = (usersRaw?.items ?? []).reduce((acc: Record<number, any>, item) => {
		acc[item.userId] = { ...item };
		return acc;
	}, {});

	const r = await getByRowTypeServer(
		RIASRecordType.APPROVEDBODY,
		2000,
		"pk, #address, #approved, #coordinator, #deputy, #expires, #name, #riasId, #terminated",
		{
			"#address": "address",
			"#approved": "approved",
			"#coordinator": "coordinator",
			"#deputy": "deputy",
			"#expires": "expires",
			"#name": "name",
			"#riasId": "riasId",
			"#terminated": "terminated",
		},
	);

	if (!r?.items) {
		throw new Error("No approved bodies returned from the database");
	}

	const approvedBodies =
		r?.items.filter(ab => ab?.riasId && ab.expires) ?? ([] as any[]);

	approvedBodies.sort((a, b) => a?.riasId - b?.riasId);

	const r2 = await getByRowTypeServer(
		RIASRecordType.SUBSCRIPTION,
		2000,
		"pk,  sk,  approvedBodyId,  expires,  joined, suspended, #name,   scheme,  #status",
		{
			"#name": "name",
			"#status": "status",
		},
	);

	const subs = r2?.items?.filter(ab => ab.approvedBodyId) ?? ([] as any[]);

	subs.sort((a, b) => a.name.localeCompare(b.name));

	for (const slug of schemeSlugs) {
		const scheme = schemeData(slug);

		if(!scheme) continue;
		if (subs.filter(s => s.scheme === slug).length === 0) continue;

		const abIds = subs
			.filter(s => s.scheme === slug)
			.map(s => parseInt(s.approvedBodyId));

		// prettier-ignore
		const schemeEle = root
			.ele( "Scheme" )
			.ele( "SchemeNo" ).txt( scheme.schemeNumber ?? "" ).up()
			.ele( "SchemeTitle" ).txt( scheme.fullName ).up();

		const abXML: ReturnType<typeof fragment>[] = [];

		for (const ab of approvedBodies.filter(ab =>
			abIds.includes(parseInt(ab.pk.split("#")[1])),
		) ?? [] ) {
			let regNo = `${scheme.prefix}-DB-${ab.riasId?.toString().padStart(5, "0")}`;

			// prettier-ignore
			const approvedBody = fragment().ele( 'ApprovedBody' )
				.ele( 'RegistrationNo' ).txt( regNo ).up()
				.ele( 'SchemeNo' ).txt( scheme.schemeNumber ?? "" ).up()
				.ele( 'TradingName' ).txt( ab.address.title ).up()
				.ele( 'ContactFirstname' ).txt( ab.coordinator.name.split( ' ' )[0] ).up()
				.ele( 'ContactSurname' ).txt( ab.coordinator.name.split( ' ' ).slice( 1 ).join( ' ' ) ).up()
				.ele( 'ContactEmail' ).txt( ab.coordinator?.email ?? '' ).up()
				.ele( 'ContactPhone' ).txt( ab.coordinator?.telephone ?? '' ).up()
				.ele( 'TradingAddressLine1' ).txt( ab.address['address-1'] || '' ).up()
				.ele( 'TradingAddressLine2' ).txt( ab.address['address-2'] || '' ).up()
				.ele( 'TradingAddressLine3' ).txt( ab.address.county || '' ).up()
				.ele( 'TradingAddressLine4' ).txt( ab.address.town || '' ).up()
				.ele( 'TradingAddressPostcode' ).txt( ab.address.postcode || '' ).up()

			for (let i = 1; i <= 32; i++) {
				// prettier-ignore
				approvedBody.ele( 'ApprovedBodyLA' )
					.ele( 'RegistrationNo' ).txt( regNo ).up()
					.ele( 'LACode' ).txt( i.toString() ).up()
					.up();
			}
			approvedBody
				.ele("RegistrationPeriods")
				.ele("RegistrationNo")
				.txt(regNo)
				.up()
				.ele("ApprovalDate")
				.txt(formatDate(ab.approved))
				.up()
				.ele("ExpiryDate")
				.txt(formatDate(ab.expires))
				.up()
				.up();

			abXML.push( approvedBody );
		}
		abXML.forEach(ab => schemeEle.import(ab));

		const certifierXML: ReturnType<typeof fragment>[] = [];
		for (const sub of subs.filter(s => s.scheme === slug)) {
			const ab = approvedBodies.find(
				a => a.pk === `APPROVEDBODY#${sub.approvedBodyId}`,
			);
			let abRegNo = `${scheme.prefix}-DB-${ab?.riasId?.toString().padStart(5, "0")}`;

			const user = users?.[parseInt(sub.pk.split("#")[1])];

			// prettier-ignore
			const certifier = fragment().ele( 'ApprovedCertifier' )
				.ele( 'RegistrationNo' ).txt( sub.name ).up()
				.ele( 'ABRegistrationNo' ).txt( abRegNo ).up()
				.ele( 'SchemeNo' ).txt( scheme.schemeNumber ?? "" ).up()
				.ele( 'TypeId' ).txt( '1' ).up()
				.ele( 'CertifierFirstname' ).txt( user?.firstName ?? '' ).up()
				.ele( 'CertifierSurname' ).txt( user?.lastName ?? '' ).up()
				.ele( "RegistrationPeriods" )
				.ele( "RegistrationNo" ).txt( sub.name ).up()
				.ele( "ApprovalDate" ).txt( formatDate( sub.joined ) ).up()
				.ele( "ExpiryDate" ).txt( formatDate( sub?.suspended ? Math.min( sub.suspended, sub.expires ) : sub.expires ) ).up()
				.up();

			certifierXML.push(certifier);
		}
		certifierXML.forEach(c => schemeEle.import(c));
	}

	// prettier-ignore
	const xml = root.end( { prettyPrint: true } );
	const hash = crypto.createHash("sha256").update(xml).digest("hex");

	// Assume no changes if no record or hash matches
	const last = await getLastBsdExport();

	if (last && last.hash === hash) {
		return { result: "skipped", date: last.date, hash };
	}

	const date = Date.now();

	await uploadFile(BSD_EXPORT_KEY, xml, "application/xml");

	await updateDB("BSD", "BSD", { hash, date });

	await sendEmail({
		templateName: "register-update",
		props: {},
		to: "registerupdates@gov.scot",
		attachments: [
			{
				FileName: BSD_EXPORT_KEY,
				ContentType: "application/xml",
				RawContent: Buffer.from(xml),
			},
		],
		bcc: SEND_TO_DEV,
	});

	return { result: "updated", date, hash };
};
